import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Wrench,
  Zap,
  Home,
  Paintbrush,
  Hammer,
  ArrowRight,
  CheckCircle,
  Layers,
  Sparkles,
  TreeDeciduous,
  Waves,
  Wind,
  Bug,
  Building2,
  Droplets,
  Sofa,
  ClipboardCheck,
  HelpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
  plumbing: {
    name: "Plumbing",
    icon: Wrench,
    description: "Expert plumbing solutions for leaks, clogs, installations, and repairs. Our certified plumbers handle everything from minor fixes to major installations.",
    image: "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["24/7 Emergency Service", "Licensed Plumbers", "Upfront Pricing", "Warranty Included"],
  },
  electrical: {
    name: "Electrical",
    icon: Zap,
    description: "Safe and reliable electrical services including wiring, outlets, lighting, and panel upgrades. All work performed by licensed electricians.",
    image: "https://images.pexels.com/photos/8488029/pexels-photo-8488029.jpeg",
    features: ["Code Compliant", "Safety Inspections", "Energy Efficient Solutions", "Licensed Electricians"],
  },
  roofing: {
    name: "Roofing",
    icon: Home,
    description: "Comprehensive roofing services from inspections to repairs and maintenance. Protect your property with our professional roofing solutions.",
    image: "https://images.unsplash.com/photo-1628002580365-f3c0a322d577?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Free Inspections", "Storm Damage Repair", "Preventive Maintenance", "Quality Materials"],
  },
  tiling: {
    name: "Tiling",
    icon: Layers,
    description: "Professional tiling services for floors, walls, backsplashes, and more. Transform your space with beautiful, durable tilework.",
    image: "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Custom Designs", "Waterproofing", "Grout Repair", "Material Selection Help"],
  },
  carpentry: {
    name: "Carpentry",
    icon: Hammer,
    description: "Custom carpentry and woodwork services including repairs, installations, and custom builds. Craftsmanship you can count on.",
    image: "https://images.unsplash.com/photo-1628002580365-f3c0a322d577?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Custom Builds", "Furniture Repair", "Deck & Fence Work", "Trim & Molding"],
  },
  painting: {
    name: "Painting",
    icon: Paintbrush,
    description: "Interior and exterior painting services that transform your space. Professional preparation, quality paints, and flawless finishes.",
    image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Color Consultation", "Premium Paints", "Surface Preparation", "Clean & Tidy Work"],
  },
  remodeling: {
    name: "Remodeling",
    icon: Home,
    description: "Full-service remodeling for bathrooms, kitchens, and more. Turn your vision into reality with our experienced renovation team.",
    image: "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Design Assistance", "Project Management", "Quality Materials", "Timeline Commitment"],
  },
  cleaning: {
    name: "Cleaning",
    icon: Sparkles,
    description: "Professional cleaning services for homes and businesses. Deep cleaning, move-in/out cleaning, post-construction cleanup, and more.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Deep Cleaning", "Eco-Friendly Products", "Flexible Scheduling", "Background Checked Staff"],
  },
  lawn_care: {
    name: "Lawn Care",
    icon: TreeDeciduous,
    description: "Complete lawn and landscape services to keep your property looking its best. Mowing, landscaping, irrigation, and tree services.",
    image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Regular Maintenance", "Landscaping Design", "Irrigation Systems", "Tree & Shrub Care"],
  },
  pool_maintenance: {
    name: "Pool Maintenance",
    icon: Waves,
    description: "Keep your pool crystal clear and ready for swimming. Regular cleaning, equipment repair, and seasonal opening/closing services.",
    image: "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Weekly Cleaning", "Chemical Balancing", "Equipment Repair", "Pool Opening/Closing"],
  },
  hvac: {
    name: "HVAC",
    icon: Wind,
    description: "Heating, ventilation, and air conditioning services for optimal comfort. Installation, repair, and maintenance for all HVAC systems.",
    image: "https://images.unsplash.com/photo-1758798157512-f0a864c696c9?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["AC & Heating Repair", "System Installation", "Duct Cleaning", "Preventive Maintenance"],
  },
  pest_control: {
    name: "Pest Control",
    icon: Bug,
    description: "Protect your property from unwanted pests. Comprehensive pest control services for insects, rodents, and more.",
    image: "https://images.pexels.com/photos/32055757/pexels-photo-32055757.jpeg",
    features: ["General Pest Control", "Termite Treatment", "Rodent Control", "Preventive Programs"],
  },
  janitorial: {
    name: "Janitorial",
    icon: Building2,
    description: "Professional janitorial services for commercial properties. Keep your office, retail space, or facility spotless and welcoming.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Daily/Weekly Service", "Floor Care", "Sanitization", "Custom Schedules"],
  },
  window_cleaning: {
    name: "Window Cleaning",
    icon: Droplets,
    description: "Crystal clear windows for homes and businesses. Professional window cleaning for all types of properties.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Residential Windows", "Commercial High-Rise", "Screen Cleaning", "Hard Water Removal"],
  },
  metalwork: {
    name: "Metalwork & Welding",
    icon: Hammer,
    description: "Professional metalwork and welding services for residential and commercial properties. Gates, railings, security grilles, and custom fabrication.",
    image: "https://images.unsplash.com/photo-1683830932134-2d06849534eb?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Welding & Fabrication", "Gates & Railings", "Security Grilles", "Metal Repairs"],
  },
  furniture: {
    name: "Furniture Assembly",
    icon: Sofa,
    description: "Fast, hassle-free assembly of flat-pack and custom furniture. We also mount TVs and shelves, disassemble for moving, and repair wobbly pieces.",
    image: "https://images.unsplash.com/photo-1581539250439-c96689b516dd?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Flat-Pack Assembly", "TV & Wall Mounting", "Disassembly & Moving", "Furniture Repair"],
  },
  inspection: {
    name: "Building Inspection",
    icon: ClipboardCheck,
    description: "Buying or renting a property? Our pre-purchase building inspections give you a clear, honest report on the condition — structure, roof, plumbing, electrical and moisture — so you can make your decision with confidence.",
    image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Pre-Purchase Inspections", "Structural & Foundation Checks", "Roof & Moisture Detection", "Detailed Condition Report"],
  },
  other: {
    name: "Other Services",
    icon: HelpCircle,
    description: "Can't find what you're looking for? No problem! Tell us what you need and we'll help. We offer custom solutions for unique property maintenance needs.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: ["Custom Requests", "Flexible Solutions", "Free Consultation", "Personalized Service"],
  },
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calloutFee, setCalloutFee] = useState(250);
  const { formatMoney } = useCurrency();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const [activeTab, setActiveTab] = useState(initialCategory);

  useEffect(() => {
    fetchServices();
    fetchCalloutFee();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalloutFee = async () => {
    try {
      const response = await axios.get(`${API}/callout-fee`);
      setCalloutFee(response.data.fee);
    } catch (error) {
      console.error("Error fetching callout fee:", error);
    }
  };

  const categories = ["all", ...Object.keys(categoryInfo)];

  const filteredServices =
    activeTab === "all"
      ? services
      : services.filter((s) => s.category === activeTab);

  const groupedServices = filteredServices.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="services-page">
      <Seo
        title="Handyman Services — Plumbing, Electrical, Roofing, Furniture Assembly & More"
        description="Explore ProToCall's full range of professional handyman services across Saint Lucia and the Caribbean: plumbing, electrical, roofing, tiling, carpentry, painting, cleaning, HVAC, furniture assembly and more."
        path="/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: [
            "Plumbing",
            "Electrical",
            "Roofing",
            "Tiling",
            "Carpentry",
            "Painting",
            "Cleaning",
            "HVAC",
            "Furniture Assembly",
            "Metalwork & Welding",
          ].map((name, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: { "@type": "Service", name, provider: { "@type": "HomeAndConstructionBusiness", name: "ProToCall" } },
          })),
        }}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-block bg-orange-500/20 text-orange-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Our Services
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="services-page-title"
            >
              Residential & Commercial Services
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Comprehensive property maintenance for homes and businesses. 
              One-time service or set up recurring schedules.
            </p>
            {/* Callout Fee Banner */}
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-white">
                Only <strong>{formatMoney(calloutFee, { decimals: 0 })}</strong> Callout Fee • Flat upfront rate • No hidden charges
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="overflow-x-auto pb-4 services-scrollbar">
              <TabsList className="inline-flex gap-2 mb-12 bg-transparent h-auto p-0 min-w-max">
                {categories.map((category) => (
                  <TabsTrigger
                    key={category}
                    value={category}
                    className="px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-600 data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:border-orange-500 capitalize transition-colors whitespace-nowrap"
                    data-testid={`tab-${category}`}
                  >
                    {category === "all" ? "All Services" : categoryInfo[category]?.name || category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value={activeTab} className="mt-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                </div>
              ) : activeTab === "all" ? (
                // Show all categories grouped
                <div className="space-y-16">
                  {Object.entries(groupedServices).map(([category, categoryServices]) => (
                    <div key={category}>
                      <div className="flex items-center gap-4 mb-8">
                        {categoryInfo[category] && (
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                            {(() => {
                              const Icon = categoryInfo[category].icon;
                              return <Icon className="w-6 h-6 text-orange-500" />;
                            })()}
                          </div>
                        )}
                        <div>
                          <h2
                            className="text-2xl font-bold text-slate-900"
                            style={{ fontFamily: "Manrope, sans-serif" }}
                          >
                            {categoryInfo[category]?.name || category}
                          </h2>
                          <p className="text-slate-500 text-sm mt-1">
                            {categoryServices.length} service{categoryServices.length !== 1 ? "s" : ""} available
                          </p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categoryServices.map((service, index) => (
                          <motion.div
                            key={service.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                          >
                            <Card
                              className="bg-white border border-slate-200 hover:shadow-lg transition-shadow h-full"
                              data-testid={`service-card-${service.id}`}
                            >
                              <CardHeader>
                                <CardTitle className="text-lg font-semibold text-slate-900">
                                  {service.name}
                                </CardTitle>
                                <p className="text-sm text-slate-500">{service.description}</p>
                              </CardHeader>
                              <CardContent>
                                <Button
                                  asChild
                                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                                  data-testid={`book-service-${service.id}`}
                                >
                                  <Link to={`/booking?service=${service.id}`}>
                                    Book This Service
                                    <ArrowRight className="ml-2 w-4 h-4" />
                                  </Link>
                                </Button>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Show single category with details
                <div>
                  {categoryInfo[activeTab] && (
                    <div className="grid lg:grid-cols-2 gap-12 mb-12">
                      <div>
                        <img
                          src={categoryInfo[activeTab].image}
                          alt={categoryInfo[activeTab].name}
                          className="w-full h-[300px] object-cover rounded-xl shadow-lg"
                        />
                      </div>
                      <div>
                        <h2
                          className="text-3xl font-bold text-slate-900 mb-4"
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          {categoryInfo[activeTab].name} Services
                        </h2>
                        <p className="text-slate-600 leading-relaxed mb-6">
                          {categoryInfo[activeTab].description}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {categoryInfo[activeTab].features.map((feature) => (
                            <div key={feature} className="flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-slate-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <h3
                    className="text-xl font-semibold text-slate-900 mb-6"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Available Services
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredServices.map((service, index) => (
                      <motion.div
                        key={service.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Card
                          className="bg-white border border-slate-200 hover:shadow-lg transition-shadow h-full"
                          data-testid={`service-card-${service.id}`}
                        >
                          <CardHeader>
                            <CardTitle className="text-lg font-semibold text-slate-900">
                              {service.name}
                            </CardTitle>
                            <p className="text-sm text-slate-500">{service.description}</p>
                          </CardHeader>
                          <CardContent>
                            <Button
                              asChild
                              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                              data-testid={`book-service-${service.id}`}
                            >
                              <Link to={`/booking?service=${service.id}`}>
                                Book This Service
                                <ArrowRight className="ml-2 w-4 h-4" />
                              </Link>
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Need a Custom Solution?
          </h2>
          <p className="text-slate-600 mb-8">
            Contact us for a free consultation. We serve both residential and commercial properties.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8 rounded-md"
              data-testid="get-quote-btn"
            >
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold h-12 px-8 rounded-md"
              data-testid="call-now-btn"
            >
              <a href="tel:+17587230202">Call +17587230202</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
